<template>
  <div
    ref="lookup"
    class="rt-lookup"
    :class="[multiple ? 'is-multiple': 'is-single']"
    @click.stop="rtLookupShowHandler"
  >
    <div
      v-if="$slots.prefix"
      ref="prefix"
      class="rt-lookup-prefix"
    >
      <slot name="prefix" />
    </div>

    <div
      :class="[
        'lookup-wrap',
        isLink ? 'is-link' : '',
        showClose ? 'lookup-close' : ''
      ]"
      @mouseenter="inputHovering = true"
      @mouseleave="inputHovering = false"
    >
      <div
        v-if="list && list.length > 0"
        class="lookup-main"
      >
        <span
          ref="tag"
          :style="{
            position: 'absolute',
            opacity: 0,
            'pointer-events': 'none'
          }"
        >
          <rt-tag
            v-for="(item, index) in list"
            :key="index"
            size="medium"
            :disabled="computedDisabled"
            :closable="!computedDisabled"
            class="rt-tag-box"
            type="outline"
            :text-type="isLink ? 'primary' : ''"
            @close="() => {}"
          >{{ item[alias.name] }}</rt-tag>
        </span>

        <template v-if="max && list.length > maxIndex">
          <div class="tag">
            <rt-tag
              v-for="(item, index) in list.slice(0, maxIndex)"
              :key="index"
              :disabled="computedDisabled"
              :closable="!computedDisabled"
              type="outline"
              size="medium"
              :text-type="isLink ? 'primary' : ''"
              @click.native.stop="onTagClick(item, item[alias.name])"
              @close="del(index)"
            >
              {{ item[alias.name] }}
            </rt-tag>
            <rt-tooltip
              :content="contentPopover"
              placement="bottom"
            >
              <rt-tag
                :disabled="computedDisabled"
                :closable="!computedDisabled"
                type="outline"
                size="medium"
                :text-type="isLink ? 'primary' : ''"
                @click.native.stop="onTagClick(item, item[alias.name])"
                @close="del(list.length - 1)"
              >
                +{{ list.length - maxIndex }}
              </rt-tag>
            </rt-tooltip>
          </div>
        </template>
        <template v-else-if="$scopedSlots.popover">
          <rt-popover
            v-for="(item, index) in list"
            :key="index"
            v-bind="computedPopoverOptions"
          >
            <rt-tag
              slot="reference"
              :disabled="computedDisabled"
              :closable="multiple ? !computedDisabled : false"
              type="outline"
              size="medium"
              :text-type="isLink ? 'primary' : ''"
              @click.native.stop="onTagClick(item, item[alias.name])"
              @close="del(index)"
            >
              {{ item[alias.name] }}
            </rt-tag>
            <slot
              :item="item"
              name="popover"
            ></slot>
          </rt-popover>
        </template>
        <template v-else>
          <rt-tag
            v-for="(item, index) in list"
            :key="index"
            :disabled="computedDisabled"
            :closable="multiple ? !computedDisabled : false"
            type="outline"
            size="medium"
            :text-type="isLink ? 'primary' : ''"
            @click.native.stop="onTagClick(item, item[alias.name])"
            @close="del(index)"
          >
            {{ item[alias.name] }}
          </rt-tag>
        </template>
      </div>
      <div
        v-else-if="multiple"
        class="select-placeholder"
      >
        <span>{{ computedPlaceholder }}</span>
      </div>
      <rt-input
        v-if="!multiple"
        v-model="inputText"
        :class="{'rt-lookup-allow-input': allowInput}"
        type="autocomplete"
        :fetch-suggestions="fetchSuggestions"
        :trigger-on-focus="false"
        :show-suffix-icon="false"
        :clearable="false"
        :highlight-first-item="computedHighlightFirstItem"
        :readonly="!allowInput"
        :placeholder="computedPlaceholder"
        :disabled="computedDisabled"
        @select="addValue"
        @input="inputHandler"
        @change="changeHandler"
        @mouseenter.native="inputHovering = true"
        @mouseleave.native="inputHovering = false"
        @keydown.native="keydownHandler"
      ></rt-input>
    </div>

    <rt-icon
      v-if="showClose"
      class="rt-lookup-suffix rt-lookup-suffix_close"
      :name="computedIconName"
      :class="{ 'rt-lookup-clearicon-position': allowInput }"
      @click.stop="clear"
      @mouseenter.native="inputHovering = true"
      @mouseleave.native="inputHovering = false"
    />

    <rt-tooltip
      :disabled="!iconContent"
      :content="iconContent"
      placement="right"
    >
      <rt-icon
        v-show="!computedDisabled"
        class="rt-lookup-suffix rt-lookup-suffix_lookup"
        :name="suffixIconName"
        @click.stop="show"
        @mouseenter.native="inputHovering = false"
        @mouseleave.native="inputHovering = false"
      />
    </rt-tooltip>

    <rt-lookup-view
      ref="lookupView"
      :fetch="fetchRecord"
      :display-fields="displayFields"
      :list="list"
      :multiple="multiple"
      :title="title"
      :size="size"
      :total="total"
      :alias="alias"
      :keep-selected="keepSelected"
      :page-fields="innerPageFields"
      v-bind="$attrs"
      @confirm="confirm"
      @clear="dialogClear"
    />
  </div>
</template>

<script>
import RtIcon from '../icon/rtIcon';
import RtLookupView from './rtLookupView';
import RtInput from '../form/rtInput.vue';
import RtTag from '../tag/rtTag.vue';
import RtTooltip from '../tooltip/rtTooltip.vue';
import mixin from './mixin.js';
import xpcConfig from '../../config';
import _ from 'lodash';

export default {
  name: 'RtLookup',
  components: {
    RtIcon,
    RtLookupView,
    RtInput,
    RtTag,
    RtTooltip
  },
  mixins: [mixin],
  computed: {
    computedEmptyPlaceholder () {
      return this.emptyPlaceholder || xpcConfig.get('emptyPlaceholder');
    },
    computedPlaceholder () {
      if (this.list.length > 0) {
        return '';
      }
      if (this.placeholder && !this.computedDisabled) {
        return this.placeholder;
      } else {
        return this.computedDisabled
          ? this.computedEmptyPlaceholder
          : this.$tx('请选择', 'xpc.common.pleaseSelect');
      }
    },
    /**
     * true：高亮搜索结果第一项，不允许保存不存在的数据
     * false：高亮搜索结果，允许保存不存在的数据
     */
    computedHighlightFirstItem () {
      return !this.allowCustomValue;
    },

    showClose () {
      return (
        (!!this.inputText || this.list.length > 0) &&
        this.inputHovering &&
        !this.computedDisabled
      );
    },

    computedIconName () {
      return this.clearIconName || xpcConfig.get('clearicon.clearIconName');
    }
  },

  mounted () {
    this.$nextTick(() => {
      if (!this.inputSizeObserver) {
        this.inputSizeObserver = new ResizeObserver(
          _.throttle(entries => {
            entries.forEach(entry => {
              this.inputWidth = entry.contentRect.width;
            });
          }),
          500
        );
        this.inputSizeObserver.observe(this.$refs.lookup);
      }

      if (
        this._events &&
        this._events['click'] &&
        this._events['click'].length > 0
      ) {
        this.isLink = true;
      }
    });
  },
  beforeDestroy () {
    if (this.inputSizeObserver) this.inputSizeObserver.disconnect();
  },
  methods: {
    toEmptyList () {
      this.list = [];
    },
    keydownHandler (event) {
      const keyCode = event.keyCode;
      if (keyCode === 8 && this.list.length > 0 && !this.inputText) {
        this.list.pop();
        if (this.multiple) {
          this.$emit('input', this.list);
        } else {
          this.$emit('input', this.inputText);
        }
      }
    },
    changeHandler (val) {
      if (this.allowCustomValue && this.$xpc.isString(val)) {
        this.inputText = val;
        this.toEmptyList();
      } else {
        if (this.timer) clearTimeout(this.timer);
        this.timer = this.$setTimeout(() => {
          this.inputText = null;
        }, 200);
      }
    },
    inputHandler (val) {
      if (this.allowCustomValue) {
        this.$emit('input', val);
      }
    },
    /**
     * @public 向 lookup 输入框中添加值
     */
    addValue (item) {
      const obj = {};
      const { alias, multiple } = this;
      let id = '';
      let name = '';
      if (multiple) {
        this.list = [...this.list, ...item];
        this.$emit('input', this.list);
        this.$emit('change', this.list);
        this.$emit('select', this.list);
      } else {
        id = item ? item[alias.id] : '';
        name = item ? item[alias.name] : '';
        obj[alias.id] = id;
        obj[alias.name] = name;
        this.$emit('input', obj);
        this.$emit('change', obj);
        this.$emit('select', obj);
      }
    },
    // 标签点击事件
    onTagClick (item, text) {
      this.$emit('click', item, text);
    },
    del (index) {
      if (this.computedDisabled) {
        return;
      }
      this.list.splice(index, 1);
      this.$emit('input', this.list);
      this.$emit('confirm', this.list);
    },
    clear () {
      if (this.computedDisabled) {
        return;
      }
      if (this.multiple) {
        this.toEmptyList();
        this.$emit('input', this.list);
        this.$emit('confirm', this.list);
        this.dispatch('ElFormItem', 'el.form.change', this.list);
      } else {
        this.toEmptyList();
        this.$emit('input', null);
        this.$emit('confirm', null);
        this.dispatch('ElFormItem', 'el.form.change', null);
      }
    },
    confirm (selected) {
      const obj = {};
      const { multiple, alias } = this;
      let id = '';
      let name = '';
      if (multiple) {
        this.list = this.$refs.lookupView.selectList;
        this.$emit('input', this.list);
        this.$emit('confirm', this.list);
        this.dispatch('ElFormItem', 'el.form.change', this.list);
      } else {
        if (selected) {
          id = selected[alias.id];
          name = selected[alias.name];
          obj[alias.id] = id;
          obj[alias.name] = name;
          this.inputText = name;
          this.$emit('input', obj);
          this.$emit('confirm', obj);
          this.dispatch('ElFormItem', 'el.form.change', obj);
        } else {
          this.toEmptyList();
          this.$emit('input', null);
        }
      }
      this.selected = null;
      this.$refs.lookupView.$refs.table.$refs.table.clearSelection();
    }
  }
};
</script>



// WEBPACK FOOTER //
// rtLookup.vue?d1eff254